// src/components/onboarding/QuickSetup.tsx
// Single-screen quick setup — name, email, phone → straight to dashboard.
// No second "here's what you can do" screen. Store listing handles expectations.

import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserProfile } from "@/lib/storage";
import { Sparkles } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import appIcon from "@/assets/app-icon.png";

interface Props {
  profile: UserProfile;
  onChange: (profile: UserProfile) => void;
  onComplete: () => void;
  /** Jump to sign-in (e.g. returning users who skip local setup). */
  onJumpToSignIn?: () => void;
}

export default function QuickSetup({ profile, onChange, onComplete, onJumpToSignIn }: Props) {
  const update = (field: keyof UserProfile["personal"], value: string) => {
    onChange({ ...profile, personal: { ...profile.personal, [field]: value } });
  };

  const canContinue =
    profile.personal.fullName.trim().length > 0 &&
    profile.personal.email.trim().length > 0;

  const handleComplete = () => {
    trackEvent("quick_setup_completed", {
      has_phone: profile.personal.phone ? "yes" : "no",
    });
    onComplete();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Hero */}
      <div className="px-6 pt-6 pb-5 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <img src={appIcon} alt="Jobily" className="w-14 h-14 rounded-2xl mx-auto mb-3" />
          <h2 className="font-display font-bold text-xl mb-1">Welcome to Jobily</h2>
          <p className="text-sm opacity-90">Set up in 30 seconds. Start applying faster.</p>
          {onJumpToSignIn && (
            <p className="text-xs opacity-90 mt-3">
              Already have an account?{" "}
              <button
                type="button"
                onClick={onJumpToSignIn}
                className="font-semibold underline underline-offset-2 hover:opacity-100 opacity-95"
              >
                Sign in
              </button>
            </p>
          )}
        </motion.div>
      </div>

      <div className="flex-1 px-5 py-5 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-4"
        >
          <div>
            <h3 className="font-display font-bold text-sm text-foreground">The basics</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Just enough to start auto-filling applications.
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="fullName" className="text-xs font-medium text-muted-foreground">
                Full Name *
              </Label>
              <Input
                id="fullName"
                placeholder="Jane Doe"
                value={profile.personal.fullName}
                onChange={(e) => update("fullName", e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs font-medium text-muted-foreground">
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="jane@example.com"
                value={profile.personal.email}
                onChange={(e) => update("email", e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-xs font-medium text-muted-foreground">
                Phone
              </Label>
              <Input
                id="phone"
                placeholder="+1 (555) 000-0000"
                value={profile.personal.phone}
                onChange={(e) => update("phone", e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          <div className="rounded-lg bg-muted/50 border border-border p-3">
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">No account needed.</span>{" "}
              You get 24 hours of free autofill access. Add more details anytime from your profile.
            </p>
          </div>
        </motion.div>
      </div>

      {/* CTA */}
      <div className="px-5 py-4 border-t border-border">
        <Button
          className="w-full gap-2 h-11 text-base font-semibold"
          onClick={handleComplete}
          disabled={!canContinue}
        >
          Start Applying <Sparkles className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
